import type {
  BOOLEAN,
  KB_TYPE_EMAIL,
  KB_TYPE_NUMBER,
  KB_TYPE_PHONE,
  MODEL_LOOKUP_RECIPIENT,
  STRING,
  UI_TYPE_CHECK,
  UI_TYPE_SWITCH,
  UI_TYPE_TELEPHONE,
  UI_TYPE_TEXT,
  UI_TYPE_TIME,
  UI_TYPE_TXC_LIST,
  UI_TYPE_SURGEON_LIST,
  UI_TYPE_BINARY,
  UI_TYPE_MULT_OPTION,
  UI_TYPE_MODEL_LOOKUP,
  UI_TYPE_MODEL_REFERENCE,
  UI_TYPE_SELECT_OPTION,
  UI_TYPE_RADIO,
  UI_TYPE_DATE,
  UI_TYPE_CALCULATED,
} from '../Redux/Forms/FormTypes';
import type {
  CaseListColumn,
  CaseListColumnPreference,
  ChatroomFilter,
  DonorSortBy,
  TemplatePreferences,
} from 'flowhawk-core';

export type { CaseListColumn, CaseListColumnPreference, ChatroomFilter, DonorSortBy };

export type AppError = {
  message: string;
  label: string;
};

export const defaultOrgPermissions = {
  ALLOW_LOCAL_UPLOAD: true,
  ALLOW_DONOR_VIEW: false,
};

export type ChatSocketStatus = 'unknown' | 'connecting' | 'connected' | 'error';
export type ChatSocketError = 'generic' | 'auth_failed';

export type ChatroomSort = 'DEFAULT' | 'ALPHABETICAL' | 'BADGE_COUNT' | 'MY_ACTIVE' | 'CREATE_DATE';

export type ProfileDonorSort = 'DEFAULT' | 'MOST_RECENT' | 'BY_STATUS_UPDATE' | 'BY_REF_ID' | 'BY_UNOS_ID';

export type SortDirection = 'asc' | 'desc';

export type DonorFilter = {
  status: string;
  // TODO: finish removing donorType from codebase
  // must update txp-core before removing from DonorFilter
  donorType: string;
};

export type ChatroomType =
  | 'Standard'
  | 'Case'
  | 'Templated Case'
  | 'Donor'
  | 'Main Donor'
  | 'CDS Offer'
  | 'Research Offer'
  | 'Donor Only'
  | 'Follower Group';

// NOTE: reflects database enum E_ACCESS_LEVEL
export const AccessLevels = Object.freeze({
  guest: 'Guest',
  basicUser: 'BasicUser',
  orgMember: 'OrgMember',
  orgManager: 'OrgManager',
  sales: 'Sales',
  sysAdmin: 'SysAdmin',
  archived: 'Archived',
});

export type AccessLevel = (typeof AccessLevels)[keyof typeof AccessLevels];

// Notification preference types (camelCase - txp-core handles conversion)
export type NotificationDeliveryMethod = 'push' | 'sms' | 'email';

export type NotificationPreference = {
  enabled: boolean;
  deliveryMethods: NotificationDeliveryMethod[];
  sound: string;
  dndOverride: boolean;
  asCritical: boolean;
};

export type NotificationType =
  | 'newMessagesInRooms'
  | 'repliesToMyMessages'
  | 'reactionsAnyMessage'
  | 'reactionsMyMessages'
  | 'addedAsCaseAdmin'
  | 'addedToChatroom'
  | 'mentions'
  | 'priorityMessages'
  | 'taskUpdated'
  | 'taskPastDue'
  | 'taskEditOverdue'
  | 'taskViewOverdue';

export type NotificationPreferences = {
  [K in NotificationType]?: NotificationPreference;
};

export type UserProfile = {
  userId: number;
  phone: string;
  firstName?: string;
  lastName?: string;
  email: string;
  emailUpdate?: string;
  isEmailVerified: boolean;
  registrationPrompt: boolean;
  profilePicture?: string;
  profilePictureThumbnail?: string;
  department?: string;
  organizationalRole?: string;
  organPrograms?: string[];
  organizationId?: number;
  organizationName: string;
  verifiedOrgMember: boolean;
  templatePreferences?: TemplatePreferences;
  clientPermissions: any;
  communicationPreferences?: string;
  chatroomSort: ChatroomSort;
  chatroomFilter: ChatroomFilter;
  donorSort: ProfileDonorSort;
  donorFilter: DonorFilter;
  globalDoNotDisturbEnd: string;
  globalAlertSound: string;
  globalHighPriorityAlertSound: string;
  emailNotification: boolean;
  smsNotification: boolean;
  smsSenderNumber: string;
  adminNotification: boolean;
  notificationPreferences?: NotificationPreferences;
  accessLevel?: string;
  license?: string;
  sentCount?: number;
  readCount?: number;
  caseListColumns?: CaseListColumnPreference[];
};

export type UserProfileWithSelection = UserProfile & {
  isSelected?: boolean;
  preference?: string;
};

export type MemberProfile = {
  profile: UserProfile;
};

export type MemberProfileV2 = {
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
  profilePicture?: string;
  profilePictureThumbnail?: string;
  userId: number;
  organizationName: string;
  organizationalRole?: string;
  department?: string;
};

export type MemberProfileData = {
  profile: MemberProfileV2;
  membershipStatus?: string;
};

export type ContactsMap = {
  [key: string]: MemberProfileData;
};

export type Profile = UserProfile | MemberProfileV2;

export type RemoteMemberProfile = {
  user_id: number;
  phone: string;
  first_name?: string;
  last_name?: string;
  email: string;
  profile_picture?: string;
  profile_picture_url?: string;
  presigned_profile_picture_url?: string;
  presigned_profile_picture_thumbnail_url?: string;
  department?: string;
  organizational_role?: string;
  organ_programs?: string[];
  template_preferences: any;

  organization_id?: number;
  organization_name: string;
  is_email_verified: boolean;
  registration_prompt: boolean;

  client_permissions: any;
  communication_preferences?: string;
  chatroom_sort: ChatroomSort;
  donor_sort?: ProfileDonorSort;
  donor_filter?: DonorFilter;
  global_do_not_disturb_end: string;
  global_alert_sound: string;
  global_high_priority_alert_sound: string;
  email_notification: boolean;
  sms_notification: boolean;
  sms_sender_number?: string;
  admin_notification: boolean;
  notification_preferences?: NotificationPreferences;

  access_level?: string;
  license?: string;

  sent_count: 0;
  read_count: 0;
  case_list_columns?: Array<{ key: string; visible: boolean; width?: number }>;
};

export type RemoteUserProfile = {
  user_id: number;
  access_level?: string;
  license?: string;
  phone: string;
  first_name?: string;
  last_name?: string;
  email: string;
  email_update?: string;
  profile_picture?: string;
  presigned_profile_picture_url?: string;
  presigned_profile_picture_thumbnail_url?: string;
  department?: string;
  organizational_role?: string;
  organ_programs?: string[];
  template_preferences: any;

  organization_id?: number;
  organization_name: string;
  verified_organization_member: boolean;
  is_email_verified: boolean;
  registration_prompt: boolean;

  client_permissions: any;
  communication_preferences?: string;
  chatroom_sort: ChatroomSort;
  donor_sort?: ProfileDonorSort;
  donor_filter?: DonorFilter;
  global_do_not_disturb_end: string;
  global_alert_sound: string;
  global_high_priority_alert_sound: string;
  email_notification: boolean;
  sms_notification: boolean;
  sms_sender_number: string;
  admin_notification: boolean;
  notification_preferences?: NotificationPreferences;
  sent_count: number;
  read_count: number;
  case_list_columns?: Array<{ key: string; visible: boolean; width?: number }>;
};

export type RemoteExternalUser = {
  external_user_id: number;
  full_name: string;
  phone?: string;
  email?: string;
  details?: string;
  acin_data?: any;
};

export type ExternalUser = {
  userId: number;
  fullName: string;
  phone?: string;
  email?: string;
  details?: string;
  acinData?: any;
  firstName?: string;
  lastName?: string;
};

export type PendingRemovedUser = {
  displayName: string;
  memberId: number;
  isExternalUser: boolean;
};

export type Team = {
  teamId: number;
  creatorId: number;
  teamName: string;
  shared: boolean;
  teamMembers: Array<UserProfile>;
  rights?: Rights;
};

export type LastMessage = {
  textContent: string;
  sentTime: string;
};

// When messageId is a number, it refers to a saved message
// When messageId is a string, it refers to the uuid of an unsaved message
export type MessageId = number | string;
export const getMessageIdNumber = (messageId: MessageId): number => {
  return typeof messageId === 'string' ? parseInt(messageId, 10) : messageId;
};

export type MessageSendStatus = 'pending' | 'failed' | 'delivered';

export const MessageTypes = Object.freeze({
  unknown: 0,
  text: 1,
  jpeg: 2,
  jpg: 3,
  png: 4,
  gif: 5,
  mp4: 6,
  pdf: 7,
  mov: 8,
  /* 9 - 16: reserved */
  addMember: 17,
  removeMember: 18,
  inviteUser: 19,
  uninviteUser: 20,
  joinByInvitation: 21,
  askAlanInvoke: 22,
  askAlanDonor: 23,
  inviteGuest: 24,
  removeGuest: 25,
  guestJoin: 26,
  guestLeave: 27,
  chatroomCreated: 28,
  askAlanRecipient: 29,
  askAlanCandidate: 30,
  askAlanVoid: 31,
  multipartMedia: 32,
  donorTask: 33,
  openCloseDonor: 34,
  callUser: 35,
  donorTaskExpiration: 36,
  docx: 37,
  doc: 38,
  xlsx: 39,
  xls: 40,
  csv: 41,
  caseNotification: 42,
  xmatScreen: 43,
  xmatTrends: 44,
  xmatHistory: 45,
  updateRoomIcon: 46,
  xmatAdmin: 47,
  xmatNotes: 48,
  xmatSumExt: 49,
  xmatSumInt: 50,
  xmatCaseSum: 51,
});

export const acceptableFileTypes: string = '.png, .jpg, .jpeg, .mp4, .mov, .pdf, .xml, .doc, .docx, .csv, .xls, .xlsx';

export type MediaType = 'unknown' | 'video' | 'image' | 'application' | 'downloadOnly';

export type MessageType = (typeof MessageTypes)[keyof typeof MessageTypes];

export type MimeTypeString =
  | 'image/png'
  | 'image/jpeg'
  | 'image/jpg'
  | 'image/gif'
  | 'video/mp4'
  | 'video/quicktime'
  | 'text/csv'
  | 'application/pdf'
  | 'application/msword'
  | 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  | 'application/vnd.ms-excel'
  | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const ImageMessageTypes: MessageType[] = [
  MessageTypes.jpeg,
  MessageTypes.jpg,
  MessageTypes.png,
  MessageTypes.gif,
  MessageTypes.xmatScreen,
];
export const VideoMessageTypes: MessageType[] = [MessageTypes.mp4, MessageTypes.mov];
export const ApplicationMessageTypes: MessageType[] = [MessageTypes.pdf];
export const DownloadOnlyMessageTypes: MessageType[] = [
  MessageTypes.doc,
  MessageTypes.docx,
  MessageTypes.csv,
  MessageTypes.xls,
  MessageTypes.xlsx,
  MessageTypes.xmatTrends,
  MessageTypes.xmatHistory,
  MessageTypes.xmatAdmin,
  MessageTypes.xmatNotes,
  MessageTypes.xmatSumExt,
  MessageTypes.xmatSumInt,
  MessageTypes.xmatCaseSum,
];
export const FileMessageTypes: MessageType[] = [
  ...ImageMessageTypes,
  ...VideoMessageTypes,
  ...ApplicationMessageTypes,
  ...DownloadOnlyMessageTypes,
];
export const SystemMessageTypes: MessageType[] = [
  MessageTypes.addMember,
  MessageTypes.removeMember,
  MessageTypes.inviteUser,
  MessageTypes.uninviteUser,
  MessageTypes.joinByInvitation,
  MessageTypes.inviteGuest,
  MessageTypes.removeGuest,
  MessageTypes.guestJoin,
  MessageTypes.guestLeave,
  MessageTypes.chatroomCreated,
  MessageTypes.updateRoomIcon,
];
export const AskAlanMessageTypes: MessageType[] = [
  MessageTypes.askAlanInvoke,
  MessageTypes.askAlanDonor,
  MessageTypes.askAlanRecipient,
  MessageTypes.askAlanCandidate,
  MessageTypes.askAlanVoid,
];

export const ImageMessageStrings = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
export const VideoMessageStrings = ['video/mp4', 'video/quicktime'];
export const ApplicationMessageStrings = ['application/pdf', 'text/xml'];
export const DownloadOnlyMessageStrings = [
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
export const FileMessageTypeStrings = [
  ...ImageMessageStrings,
  ...VideoMessageStrings,
  ...ApplicationMessageStrings,
  ...DownloadOnlyMessageStrings,
];

export type PictureLocation = {
  uri: string;
  headers: {
    Apikey: string;
    Authorization: string;
  };
};

export type PostAttachmentData = {
  file_name: string;
  display_name?: string;
  file_size: number;
  chat_type_id: number;
};

export type AttachmentData = {
  mime: MimeTypeString;
  size: number;
  path: string;
  file: File;
  fileName: string;
  displayName?: string;
};

export type ChatTemporaryMessage = {
  text: string;
  attachments: Array<AttachmentData>;
  replyMessageId?: number;
  mentionedUsers: Array<number>;
  mentionedNames: Array<string>;
  filteredNames: Array<string>;
  mediaHeight: number;
  localPath?: string;
};

export type ChatTemporaryMessageMap = {
  [key: string]: ChatTemporaryMessage;
};

export type ChatroomFile = {
  id: MessageId;
  fileName: string;
  displayName?: string;
  localPath?: string;
  presignedUrl?: string;
  presignedThumbnailUrl?: string;
  messageType: MessageType;
  size: number; // in bytes
  sentTime: string;
  memberId: number;
  originalIndex?: number;
};

export type ChatroomFileMap = {
  [key: string]: ChatroomFile;
};

export type MessageReadStatus = {
  // memberId -> readTime
  [key: string]: string;
};

export type ReadStatusMap = {
  // chatId -> MessageReadStatus
  [key: string]: MessageReadStatus;
};

export type MessageAcknowledge = {
  chatId: number;
  messageId: number;
  memberId: number;
  symbol: string;
};

export type MessageAcknowledgeMap = {
  // memberId => ack obj
  [key: string]: MessageAcknowledge;
};

export type ChatroomMessage = {
  id: MessageId;
  senderId: number;
  senderName: string;
  sentTime: string;

  messageType: MessageType;

  textContent: string;
  dataContent: any;
  displayName?: string;
  fileName?: string;
  localPath?: string;
  presignedUrl?: string;
  presignedThumbnailUrl?: string;
  seenByCount: number;
  seenByCurrentUser: boolean;
  status?: MessageSendStatus; // only set for unsent messages
  ack: MessageAcknowledgeMap;
  redactTime?: string;
  editTime?: string;
  editedBy?: number;
  replyingMessageId?: number;
  replyingTextContent?: string;
  replyingMessageType?: MessageType;
  replyingDataContent?: any;
  replyingFilePath?: string;
  replyingSenderId?: number;
  parentMessageId?: number;
  caseId?: number;
  highPriority: boolean;
};

export type ChatroomMessageMap = {
  [key: string]: ChatroomMessage;
};

export type PinnedMessage = {
  id: MessageId;
  pinOrder: number;
  pinnedBy: number;
  pinnedTime: string;
  message: ChatroomMessage;
};

export type PinnedMessageMetadata = {
  id: MessageId;
  pinOrder: number;
  pinnedBy: number;
  pinnedTime: string;
  senderId: number;
  senderName?: string;
  textContent?: string;
  displayName?: string;
  fileName?: string;
  messageType: number;
};

export type PinnedMessageMap = {
  [key: string]: PinnedMessage;
};

export type ChatMembershipStatus =
  | 'Expired'
  | 'Invited'
  | 'Present'
  | 'Left'
  | 'Removed'
  | 'Archived'
  | 'Pending'
  | 'NonMember';

export type ChatroomMember = {
  profile: UserProfile | MemberProfileV2;
  membershipStatus: ChatMembershipStatus;
  startDate: string;
  endDate: string;
  doNotDisturb?: boolean;
  notificationSound?: string;
  priorityNotificationSound?: string;
  addedBy?: number;
  removedBy?: number;
  addedByName?: string;
  removedByName?: string;
};

export type ChatroomMemberMap = {
  [key: string]: ChatroomMember;
};

export type ChatroomMemberInfo = {
  id: number;
  members: ChatroomMemberMap;
  memberOrder: Array<number>;
};

export type UpdateChatroomManagerParams = {
  chatId: number;
  userId: number;
};

export type UpdateCaseAdminParams = {
  caseId: number;
  userId: number;
};

export type DonorRestrictions = {
  DONOR_DETAILS: boolean;
  DONOR_TIMELINE: boolean;
  DONOR_FILES: boolean;
};

export type RemoteChatroom = {
  chatroom_id: number;
  chatroom_name: string;
  chatroom_description: string;
  chatroom_type: string;
  managed?: boolean;
  creator_id: number;
  member_count: number;
  do_not_disturb: boolean;
  pin_number: number;
  total_message_count?: number;
  sent_count?: number;
  read_count?: number;
  cleared_count?: number;
  last_instant_message_time?: string;
  last_instant_message_content?: string;
  last_update_time: string;
  last_active_time?: string;
  donorId?: number;
  organId?: number;
  followerId?: number;
  organType?: string;
  targetOrgId?: number;
  createDate: string;
  donorClosed: boolean;
  caseName?: string;
  followerName?: string;
};

export type ChatroomInfo = {
  id: number;
  name: string;
  description: string;
  chatroomType: ChatroomType;
  creatorId: number;

  members: ChatroomMemberMap;
  memberOrder: Array<number>;
  memberCount: number;
  membersTyping: Array<number>;
  managerIds: Array<number>;

  managed: boolean;

  pinNumber?: number;

  readStatus: ReadStatusMap;

  // Timestamp of the last message status we received
  //  This helps us determine quite quickly whether we should re-render the UI or not
  lastMessageStatusReceived?: string;
  messages: ChatroomMessageMap;
  messageOrder: Array<MessageId>;
  pinnedMessages: PinnedMessageMap;
  pinnedOrder: Array<MessageId>;

  sentCount: number;
  readCount: number;
  clearedCount: number;

  lastMessage?: LastMessage;
  lastUpdateTime?: string;
  lastActiveTime?: string;
  // Timestamp of the last full (non-paginated) reload of this room's messages.
  //  Used to decide when cached messages (and their presigned media URLs) are stale enough to force a refresh.
  messagesRefreshedTime?: string;
  totalMessages: number;
  offset: number;

  files: ChatroomFileMap;
  allFiles: Array<MessageId>;
  myFiles: Array<MessageId>;
  selectedFile: ChatroomFile;

  // Id of the last failed message
  // used to detect if we need to re-render the chat screen
  lastFailedId?: string;

  donorId?: number;
  donorClosed: boolean;
  organId?: number;
  organType?: string;
  followerId?: number;

  targetOrgId?: number;

  // File name of the notification sound to use for this room
  notificationSound: string;
  priorityNotificationSound: string;

  doNotDisturb: boolean;
  createDate: string;
  caseName?: string;
  followerName?: string;

  roomIcon?: string;

  memberNames: string[];
};

export type SetNotificationSoundPayload = {
  high_priority_alert_sound?: string;
  alert_sound?: string;
};

export type ChatroomInfoMap = {
  [key: string]: ChatroomInfo;
};

export type RemoteMessageStatus = {
  instant_message_status_id: number;
  instant_message_id: number;
  chatroom_id: number;
  member_id: number;
  read_time: string;
};

export type Offer = {
  donorId: number;
  unosId: string;
  organId: number;
  matchId: string;
  organType: string;
};

export type Organization = {
  id: number;
  code: string;
  name: string;
  templatePreferences?: TemplatePreferences;
  availableTags: Array<Tag>;
};

export type SearchResult = {
  instantMessage: ChatroomMessage;
  messagePosition: number;
  chatroomId: number;
  chatroomName: string;
  totalMessageCount: number;
};

export type SearchResultMap = {
  // chat id -> SearchResult
  [key: string]: Array<SearchResult>;
};

export type ResendNotificationCache = {
  [key: MessageId]: Array<number>;
};

export type EventName =
  | 'open-create-chatroom-click'
  | 'close-create-chatroom-click'
  | 'menu-click'
  | 'open-profile-click'
  | 'open-settings-click'
  | 'open-help-click'
  | 'logout-click'
  | 'arrow-click'
  | 'bell-click'
  | 'refresh-click'
  | 'send-message-click'
  | 'search-focus'
  | 'files-click'
  | 'notification-sounds-click'
  | 'toggle-mute-click'
  | 'leave-group-click'
  | 'emoji-picker-click'
  | 'ask-alan-click'
  | 'chatroom-details-click'
  | 'sort-menu-click'
  | 'filter-menu-click'
  | 'add-people-click'
  | 'upload-media-click'
  | 'upload-offer-click'
  | 'upload-recipient-click'
  | 'case-notification-wizard-click';

export type OrganType =
  | 'Kidneys Enbloc'
  | 'Right Kidney'
  | 'Left Kidney'
  | 'Liver'
  | 'Liver Split Left'
  | 'Liver Split Right'
  | 'Left Lung'
  | 'Right Lung'
  | 'Lungs Enbloc'
  | 'Pancreas'
  | 'Heart'
  | 'Intestine';

export type Organ = {
  organId: number;
  organType: OrganType;
};

export type AppFile = {
  id: MessageId;
  name?: string;
  fileName: string;
  displayName?: string;
  messageType: MessageType;
  size: number;
  sentTime: string;
  memberId: number;
  localPath?: string;
  presignedUrl?: string;
  presignedThumbnailUrl?: string;
  originalIndex?: number;
  mime?: string;
};

export type FileMap = {
  [key: string | number]: AppFile;
};

export type MediaSwipeFile = {
  messageId?: number;
  fileName?: string;
};

export type FormValue = string | boolean | string[] | RecipientOption | null | undefined;

export function isFormValueRecipientOption(value: FormValue): value is RecipientOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'mrn' in value &&
    typeof value.mrn === 'string'
  );
}

export type FormValueMap = {
  // fieldId -> value
  [key: number]: {
    value: FormValue;
  };
};

export type TaskFormData = {
  // formId -> FormValueMap
  [key: string]: FormValueMap;
};

export type FormErrorMap = {
  [key: number]: string;
};

export type FormErrors = {
  [key: string]: FormErrorMap;
};

export type FormDefField = {
  id: number;
  title: string;
  type: typeof BOOLEAN | typeof STRING;
  uiType:
    | typeof UI_TYPE_TEXT
    | typeof UI_TYPE_SWITCH
    | typeof UI_TYPE_CHECK
    | typeof UI_TYPE_DATE
    | typeof UI_TYPE_TXC_LIST
    | typeof UI_TYPE_SURGEON_LIST
    | typeof UI_TYPE_BINARY
    | typeof UI_TYPE_MULT_OPTION
    | typeof UI_TYPE_TIME
    | typeof UI_TYPE_TELEPHONE
    | typeof UI_TYPE_MODEL_LOOKUP
    | typeof UI_TYPE_MODEL_REFERENCE
    | typeof UI_TYPE_SELECT_OPTION
    | typeof UI_TYPE_RADIO
    | typeof UI_TYPE_CALCULATED;
  kbType?: typeof KB_TYPE_EMAIL | typeof KB_TYPE_NUMBER | typeof KB_TYPE_PHONE;
  behavior?: string;
  responseOptions?: string[];
  tags: Array<string>;
  maxLines?: number;
  maxLength?: number;
  mandatory?: boolean;
  fieldName?: string;
  displayAs?: string;
  displayOnItem?: boolean;
  isTime?: boolean;
  isDate?: boolean;
  defaultValue?: string;
  pattern?: string;
  helperText?: string;
  flightTracker?: boolean;
  emailDetails?: boolean;
  referToTaskId?: number;
  referToFieldId?: number;
  modelType?: typeof MODEL_LOOKUP_RECIPIENT;
  propertyMapping?: string;
  calculationType?: string;
  adornment?: string;
};

export type FormDefSection = {
  id: number;
  title: string;
  open?: boolean;
  fields: Array<FormDefField>;
};

export type FormDefBehaviors = {
  alwaysApplicable?: boolean;
  lockWhenFinalized?: boolean;
  saveOnComplete?: boolean;
  clearOnIncomplete?: boolean;
  clearOnNotApplicable?: boolean;
};

export type FormDefData = {
  formId: string;
  behaviors?: FormDefBehaviors;
  disableBehavior?: any;
  toolbar?: any;
  sections: Array<FormDefSection>;
};

export type FormDefDataMap = {
  // formId -> FormDefData
  [key: string]: FormDefData;
};

// Organization of form definitions by task id and field id
export type CaseFormDefMap = Record<number, Record<number, FormDefField>>;

export type LabeledField = { id: string; uiType: string; label: string; value: any; fieldCleared?: boolean };

export type Task = {
  taskId: number;
  completed: boolean;
  notApplicable: boolean;
  hasNoteContent: boolean;
  lastModified: string;
  lastModifiedBy: number;
  hasTaskData: boolean;
  dueDateInterval?: number;
  previousTaskId?: number;
  dueDate?: string;
  dueDateComment?: string;
  userCanRead?: boolean;
  rights?: Rights;
  editBy?: number;
  editTime?: string;
};

export type SubscriberDetails = {
  phone: string;
  name: string;
  profilePicture?: string;
  profilePictureThumbnail?: string;
  organizationId: number;
};

export type Subscriber = {
  subscriptionId: number;
  notifiableGroupId: number;
  targetId: number;
  unsubscribed: boolean;
  unsubscriberId: number;
  subscriberId: number;
  subscriberType: string;
  subscriberInformation: SubscriberDetails;
};

export type FollowerInvite = {
  followerInviteId: number;
  followerId: number;
  email: string | null;
  phone: string | null;
  dateInvited: string | null;
  firstName: string | null;
  lastName: string | null;
  organization: string | null;
  role: string | null;
};

export type ChatroomInvite = {
  chatroomInviteId: number;
  chatroomId: number;
  email: string | null;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  organization: string | null;
  role: string | null;
};

export type FollowerGroup = {
  followerGroupId: number;
  chatroomId: number;
  name: string;
  // NOTE: canUpdate always defined on server, but not always present in responses currently
  canUpdate?: boolean;
  externalUsers: Array<ExternalUser>;
  memberUsers: Array<UserProfile>;
  invitedUsers: Array<FollowerInvite>;
  tasks: Array<number>;
};

export type FollowerGroupMap = {
  // followerId -> follower_data
  [key: number]: FollowerGroup;
};

export type FollowerPreferences = {
  name: string;
  members: Array<number>;
  teams: Array<number>;
  tasks: Array<number>;
  canEdit: boolean;
  notifyUponCreation?: boolean;
  memberPreferences?: MemberPreferenceMap;
};

type MemberPreferenceMap = {
  [id: string]: MembershipPreference;
};

type MembershipPreference = 'Required' | 'Default' | 'Optional';

export type CasePreferences = {
  followers: Array<FollowerPreferences>;
  tags?: number[];
  stage?: CaseStage | null;
  defaultCaseName?: {
    enabled: boolean;
    template: string;
  };
};

export type TaskData = {
  // fieldId -> task_data
  [key: number]: any;
};

export type TaskDataArrayItem = {
  fieldId: string;
  data: any;
};

/**
 * How a donor's identity was established. Donors written before 'pdf' existed
 * carry 'user' even when they came from a PDF.
 */
export type DonorSource = 'user' | 'pdf' | 'unos' | 'none';

export type DonorDataEntry = {
  key: string;
  value: string;
  label: string;
};

export type ApiLabField = {
  current: number | null;
  peak: number | null;
  admit: number | null;
  count: number;
};

export type DonorImportField = {
  id: string;
  label: string;
  matcher?: string; // used to be RegExp, should be defined if not 'computed'
  computed?: true; // this signifies that this field isn't directly parsed from the PDF, but rather a conditional aggregate of parsed data,
  // each computed field will be handled a little differently when parsed
  mappingOnly?: boolean; // indicates this field should only be available for mapping and not parsed in import
  matchIndex?: number;
  multipleLines?: true; // true if the 'matcher' can exist on multiple lines, and we want to join those lines together into one field
  joiner?: string; // the joiner to combine the multiple lines, if 'multipleLines' is true
  lab?: true; // labs are calculated to get admit, current, peak and count of measurements
  vital?: true; // vitals are calculated to get current and count of measurements, can also take an additional regex to match pattern of measurement
  processRegex?: string; // is used as a RegExp
  workflowField: boolean; // indicates if this is a field we will allow to map into workflow task data
  startMatch?: string; // used to be RegExp, defines start boundary for text extraction
  stopMatch?: string; // used to be RegExp, defines stop boundary for text extraction
  subMatch?: {
    linesAway: number | number[];
    matcher: string; // used to be RegExp
    untilMatch?: string; // used to be RegExp
    matchIndex: number;
  };
};

export type TaskDataMap = {
  // taskId -> TaskData
  [key: number]: TaskData;
};

export type TaskFieldIds = { taskId: number; fieldId: number };

export type SyncedFieldsMap = {
  parentToChildren: Map<string, TaskFieldIds[]>;
  childToParent: Map<string, TaskFieldIds>;
};

export type History = {
  userId: number;
  updateTime: string;
  completed?: boolean;
  notApplicable?: boolean;
  noteContent: string;
};

export type CaseTimeline = {
  timeline: CaseTimelineEvent[];
  pending: CaseTimelinePendingField[];
};

export type CaseTimelineEvent = {
  taskId: number;
  taskName: string;
  timestamp: string;
  type: string;
  userId: number;
  fieldId?: number;
  fieldLabel?: string;
  sectionName?: string;
  fieldValue?: number | string | boolean | unknown[] | object;
  fieldUiType?: FormDefField['uiType'];
};

export type CaseTimelinePendingField = {
  taskId: number;
  taskName: string;
  sectionId: number;
  sectionName: string;
  fieldId: string;
  fieldLabel: string;
};

export type DonorTask = {
  donorId: number;
  taskId: number;
  completed: boolean;
  notApplicable: boolean;
  noteContent: string;
  history: Array<History>;
  previousTaskId?: number;
  dueDateInterval?: number;
  dueDate?: string;
  dueDateComment?: string;
  lastModified: string;
  lastModifiedBy: number;
};

export enum OrganizationAlertStatus {
  New = 'NEW',
  Acknowledged = 'ACKNOWLEDGED',
}

export type CaseServiceAlert = {
  id: number;
  providerAlertId: number;
  organizationId: number;
  caseId: number;
  chatroomId: number | null;
  status: OrganizationAlertStatus;
  createdAt: string;
  lastModifiedAt: string;
  serviceName: string;
  providerOrganizationName: string;
  unosId: string;
};

export type Donor = {
  donorId: number;
  opoDonorId: string;
  highRisk: boolean;
  unosId: string;
  donorSource?: DonorSource;
  matchId?: string;
  opo?: string;
  userId: number; // user who created the donor
  orgId: number; // organization the donor belongs to
  createDate: string;
  organs: Array<Organ>;
  closed: boolean;
  files: FileMap;
  fileIds: Array<MessageId>;
  xmatFiles: FileMap;
  selectedFile?: AppFile | null;
  dob?: string;
  race?: string;
  sex?: string;
  typeOfDeath?: string;
  causeOfDeath?: string;
  weight?: string;
  height?: string;
  unetLink?: string;
  tasks?: Task[];
  taskData?: TaskDataMap; // stores any relevant task data that needs to be displayed on the task list
  pendingFiles: FileMap;
  lastModified: string;
  lastModifiedBy: number;
  lastModifiedTaskDate?: string;
  lastModifiedTaskId?: number;
  workflow?: string;
  subscribers?: Array<Subscriber>;
  followerGroups?: FollowerGroupMap;
  tags: Array<Tag>;
  notes: Array<CaseNote>;
  isAdmin: boolean;
  rights: Rights;
  hasNewServiceAlert: boolean | null;
  unread: number;
  stage: CaseStage | null;
  rank: number | null;
  sequenceNumber: number | null;
  pinNumber?: number | null;
  initialClose: string | null;
  currentLocation: string;
  alerts?: CaseServiceAlert[];
  xmatCaseId?: string | null;
  perfusionDeviceSn?: string | null;
  timeline?: CaseTimelineEntry[];
  donorAttachments: UnosDonorAttachment[];
  caseAdmins: CaseAdmin[];
  orgPermission?: Permission;
};

export type CaseAdmin = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string;
};

export type DonorMap = {
  // donorId -> Donor
  [key: number]: Donor;
};

export type StaticTask = {
  taskId: number;
  formId?: string;
  details: {
    sectionPosition?: number;
    section: string;
    description: string;
    highRisk: boolean;
  };
};

export type WorkflowTasks = {
  tasks: Array<StaticTask>;
  sectionOrder: Array<string>;
};

export type WorkflowTaskMap = {
  [key: string]: WorkflowTasks;
};

export type WorkflowAndForms = {
  workflow: Workflow;
  forms: FormReferenceData[];
};

export type CaseNote = {
  noteId: number;
  caseId: number;
  userId: number;
  createDate: string;
  lastModified: string | null;
  note: string;
};

export type CaseTimelineEntryType =
  | 'task_completed'
  | 'task_incomplete'
  | 'task_not_applicable'
  | 'task_applicable'
  | 'note_updated'
  | 'field_updated';

export type CaseTimelineEntry = {
  type: CaseTimelineEntryType;
  timestamp: string;
  userId: number;
  taskId: number;
  taskName: string;
  noteContent?: string;
  fieldId?: string | number;
  fieldLabel?: string;
  sectionName?: string;
};

export type UnosDonorAttachment = {
  id: number;
  donorId: number;
  unosId: string;
  fileId: number;
  versionId: number;
  categories: string;
  fileName: string;
  fileExtension: string;
  fileUrl: string;
  attachedDate: string;
  description: string;
};

export type Target = {
  entityType: string;
  entityId: number;
  subEntityType?: string;
  subEntityId?: number;
  cascade: boolean;
};

export type Rights = {
  read: boolean;
  update: boolean;
  delete: boolean;
};

/**
 * Resolved UI/feature permissions for the current user, returned as a flat map
 * of feature flag -> granted by the `/permissions/features` endpoint. Mirrors the
 * server-side `FeaturePermissions` type (server/src/permission/permission_types.ts).
 */
export type FeaturePermissions = {
  notificationView: boolean;
  createCases: boolean;
  templatedCase: boolean;
  teamView: boolean;
  localUpload: boolean;
  demoCase: boolean;
  createRooms: boolean;
  alertToCenter: boolean;
  showCdsOffers: boolean;
  showDirectory: boolean;
  uploadCdsOffers: boolean;
  defaultCaseToLocal: boolean;
  caseView: boolean;
  viewReplyThread: boolean;
  caseWebBeta: boolean;
  publicContact: boolean;
  workflowBuilder: boolean;
  recipientUpload: boolean;
  orDashboard: boolean;
  casesDashboard: boolean;
  vesselDashboard: boolean;
  recoveryDashboard: boolean;
  demoDashboard: boolean;
  customDashboard: boolean;
  notificationWizard: boolean;
  createCaseTypeRoom: boolean;
  createTeam: boolean;
  unosApi: boolean;
};

/** Every feature flag defaulting to `false` -- used before permissions load and on logout. */
export const DEFAULT_FEATURE_PERMISSIONS: FeaturePermissions = {
  notificationView: false,
  createCases: false,
  templatedCase: false,
  teamView: false,
  localUpload: false,
  demoCase: false,
  createRooms: false,
  alertToCenter: false,
  showCdsOffers: false,
  showDirectory: false,
  uploadCdsOffers: false,
  defaultCaseToLocal: false,
  caseView: false,
  viewReplyThread: false,
  caseWebBeta: false,
  publicContact: false,
  workflowBuilder: false,
  recipientUpload: false,
  orDashboard: false,
  casesDashboard: false,
  vesselDashboard: false,
  recoveryDashboard: false,
  demoDashboard: false,
  customDashboard: false,
  notificationWizard: false,
  createCaseTypeRoom: false,
  createTeam: false,
  unosApi: false,
};

export type AuthorizedEntity = {
  authorizedType: string;
  authorizedId: number;
  displayName: string;
};

export type Permission = {
  permissionId: number;
  targetType: string;
  targetId: number;
  subtargetType: string | null;
  subtargetId: number | null;
  cascade: boolean;
  authorizedType: string;
  authorizedId: number;
  canRead: boolean;
  canUpdate: boolean;
  canDelete: boolean;
};

export type NotifiableEvent = {
  notifiableEventId: number;
  eventDescription: string;
  sequence: number;
  eventType: string;
  targetType: string;
  subtargetType: string;
  subtargetId: number;
};

export type Tracker = {
  id: number;
  title: string;
};

export type Workflow = {
  key: string;
  name: string;
  type: string;
  ownerId: number;
  ownerType: string;
  organizationType: string;
  flat: boolean;
  progressBar: boolean;
  tasks: Array<StaticTask>;
  sectionOrder: Array<string>;
  trackers: Array<Tracker>;
  lastModifiedDate: string;
  useTaskNavigation?: boolean;
  casePreferences?: CasePreferences;
};

export type FormReferenceData = {
  formId: string;
  name: string;
  sections: {
    id: number;
    title: string;
    fields: any[];
  }[];
  behaviors?: any;
  disableBehavior?: any;
};

export type ReferenceForm = {
  domain: 'FORM';
  key: string;
  reference_data: FormReferenceData;
  comment?: string;
};

export type WorkflowTask = {
  id: number;
  description: string;
  formId: string;
  section?: string;
  highRisk?: boolean;
};

export type ReferenceWorkflow = {
  domain: 'WORKFLOW';
  key: string;
  reference_data: {
    id: number;
    name: string;
    type: 'Donor';
    ownerType: 'Organization';
    organizationType: string;
    ownerId: number;
    tasks: WorkflowTask[];
    trackers: {
      id: number;
      title: string;
      events: {
        id: number;
        eventType: string;
        targetType: string;
      };
    }[];
    parentRefId?: number;
    version?: number;
    flat: boolean;
    progressBar: boolean;
  };
  comment?: string;
  last_modified_date: string;
  case_preferences?: { preferences?: CasePreferences };
};

export type WorkflowOption = {
  key: string;
  name: string;
};

export type DonorOption = {
  id: number;
  unosId: string;
  opo?: string;
  matchId?: string;
};

export type RefDataSurvey = {
  key: string;
  name: string;
  type: string;
  ownerId: number;
  ownerType: string;
  organizationType: string;
  label?: string;
  secondaryLabel?: string;
  passMark: number;
  questions: any;
  lastModifiedDate: string;
};

export type Tag = {
  tagId: number;
  category: string;
  label: string;
  displayColors: Array<string>;
  organizationId: number | null;
};

type ComparableItem = {
  value: string;
  comp: number;
};

// NOTE: reflects database enum E_CASE_STAGE
export const CaseStageEnum = Object.freeze({
  New: 'New',
  AutoRuleOut: 'Auto Rule Out',
  Provisional: 'Provisional',
  Backup: 'Backup',
  OrganPlacedPrior: 'Organ Placed Prior',
  Primary: 'Primary',
  Accepted: 'Accepted',
  Declined: 'Declined',
  ListExhausted: 'List Exhausted',
  OR: 'OR',
  CrossClampPending: 'Cross Clamp Pending',
  PostTransplant: 'Post-Tx',
});
export type CaseStage = (typeof CaseStageEnum)[keyof typeof CaseStageEnum];

export type CaseRow = {
  donorId: number;
  unosId: string;
  opo?: string;
  matchId?: string;
  name: string;
  sex: 'M' | 'F' | 'U';
  age?: number;
  workflow: string;
  createdOn: ComparableItem;
  tags: Tag[];
  lastActivity: ComparableItem;
  nextDueDate: ComparableItem;
  tasksCompleted: number;
  tasksTotal: number;
  closed: boolean;
  highRisk: boolean;
  donor: Donor;
  lastNote: CaseNote | null;
  isAdmin: boolean;
  rights: Rights;
  hasNewServiceAlert: boolean | null;
  badgeCount: number;
  stage: CaseStage | null;
  rank: number | null;
  sequenceNumber: number | null;
  pinNumber?: number | null;
};

export type CaseRowMinimal = {
  donorId: number;
  unosId: string;
  opo?: string;
  matchId?: string;
  name: string;
  workflow: string;
  workflowName?: string;
  stage: CaseStage | null;
  tags: Tag[];
  highRisk: boolean;
  taskData?: TaskDataMap; // stores any relevant task data that needs to be displayed on the task list
};

export type TagFilterMode = 'OR' | 'AND';

export type LoadCasesParams = {
  status?: string;
  stage?: CaseStage[];
  tags?: number[];
  tagFilterMode?: TagFilterMode;
  workflows?: string[];
  riskCriteria?: boolean | null;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  page?: number;
  resultsPerPage?: number;
};

export type SectionConfig = {
  include: boolean;
  detailKeys?: Array<string>;
  taskIds?: Array<number>;
  includeArchived?: boolean;
  chatroomIds?: Array<number>;
};

export type SelectedSections = {
  caseDetails: SectionConfig;
  caseAttachments: SectionConfig;
  caseTasks: SectionConfig;
  caseChatrooms: SectionConfig;
  caseImageAppendix: SectionConfig;
};

export type DomoEmbedToken = {
  authentication: string;
};

export type DomoInsightsEmbedToken = {
  embedId: string;
  authentication: string;
};

export type DomoDashboardParams = {
  id: string;
  display: string;
  embedId: string;
  fastRefresh: boolean;
  accessLevels: Array<AccessLevel>;
  permissionId: number;
};

export type RecipientUploadRecord = {
  id: number;
  userId: number;
  numberOfRecords: number;
  statusInfo: string;
  uploadingDate: string;
};

export type RecipientOption = {
  id: number;
  mrn: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  listed_date?: string;
  sex?: string;
  organ_type: string;
  blood_type: string;
  height?: number;
  weight?: number;
  liver_measurement?: number;
  aortic_root_sizes?: number;
  unos_waitlist_id?: string;
  unos_center_code?: string;
  notes?: string;
  location?: string;
  total_lung_capacity?: number;
  meld?: number;
  bmi?: number;
  cpra?: number;
  age?: number;
  dialysis?: string;
  gender?: string;
  hbasb?: string;
  phm?: number;
  last_modified_date: string;
};

export type CandidateContent = {
  mrn: string;
  full_name: string;
  sex: string;
  age: string | number;
  race: string;
  ethnicity: string;
  height: string | number;
  weight: string | number;
  bmi: string | number;
  blood_type: string;
  primary_diagnosis: string;
  prior_transplant: boolean | string;
  esrd_time: string;
  cpra: string | number;
  diabetes: string;
  cancer: boolean | string;
  pvd: boolean | string;
  last_seen: string;
};

export type RecipientContent = {
  full_name: string;
  waitlist_number: string;
  sex: string;
  age: string;
  priority: string;
  cpra: string;
  mismatch_a: string;
  mismatch_b: string;
  mismatch_dr: string;
};

export type Service = {
  serviceId: number;
  key: string;
  name: string;
};

export type ProviderService = {
  providerServiceId: number;
  serviceId: number;
  teamId: number | null;
  serviceName: string;
  teamName: string | null;
};

export type ServiceProvider = {
  providerId: number;
  providerName: string;
  services: ProviderService[];
};

export type MutableFunction = () => void;

export enum CaseNotificationWizardStep {
  SelectCases = 1,
  SelectFields,
  SelectRooms,
  ReviewAndNotify,
}

export type CaseNotificationField = { task_id: number; field_id: number };

// CaseView types for saved case views
export type CaseStatusFilter = 'All' | 'Open' | 'Closed';

export type CasesFilterAndSorting = {
  status?: CaseStatusFilter;
  riskCriteria?: boolean | null;
  tags?: number[];
  tagFilterMode?: TagFilterMode;
  workflows?: string[];
  search?: string;
  sortBy: DonorSortBy;
  sortDirection: SortDirection;
  stage?: CaseStage[];
};

export type CaseView = {
  id: number;
  userId: number;
  organizationId: number | null;
  name: string;
  organizationName?: string | null;
  userLabel?: string | null;
  view: CasesFilterAndSorting;
  createdAt: string;
  lastModifiedAt: string;
};

export type CreateCaseViewParams = {
  organizationId: number | null;
  name: string;
  view: CasesFilterAndSorting;
};

export type UpdateCaseViewParams = CreateCaseViewParams & {
  casesViewId: number;
};

export type CaseViewInput = {
  name: string;
  shareWithOrganization: boolean;
};

export type InsightsEmbedUrl = {
  embedUrl: string;
};

// FontAwesomeIconProps currently declares the style type as CSSProperties & CSSVariables
// but it doesn't export CSSVariables, so we're just  explicitly typing it as any for now to avoid issues.
export type FaStyleProp = any;
